import React, { useEffect, useState } from "react";
import ApiData from "./ApiData";
import { Button, InputGroup, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import PageHeader from "../Common/PageHeader";
import ApiIcon from "../../assets/icons/prod_api.svg2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { faEdit } from "@fortawesome/free-regular-svg-icons";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import {
  ApiDataCollectionType,
  ApiDataType,
  useGetApiDataCollectionQuery,
  useSaveApiDataCollectionMutation,
} from "../store/slices/settingsSlice";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { proxyApiTry } from "../Common/DataImportModal/ApiConnection";

export default function ApiDataCollection({
  apiDataCollectionUUID,
  ApiProfile,
}: {
  apiDataCollectionUUID?: string;
  ApiProfile?: { Uuid: string; BaseUrl: string };
}) {
  const [jsonText, setJsonText] = useState<{
    code?: number;
    data: string;
    message?: string;
  }>();
  const [apiDataCollection, setApiDataCollection] =
    useState<ApiDataCollectionType>({
      Uuid: "",
      Label: "",
      Fields: [],
      Path: "",
      RawJson: "",
      ApiProfiles: ApiProfile ? [ApiProfile] : [],
    });
  const [selected, setSelected] = useState<number | "new">();

  const [saveApiDataCollection] = useSaveApiDataCollectionMutation();
  const handleSaveApiDataCollection = async () => {
    const res = await saveApiDataCollection({
      ...apiDataCollection,
      RawJson: jsonText?.data || "",
    });

    if ("data" in res) {
      toast.success("Data structure saved successfully");
    } else {
      toast.error("Error saving Data structure");
    }
  };

  const { data: apiDataCollectionFromAPI } = useGetApiDataCollectionQuery(
    apiDataCollectionUUID || "",
    { skip: !apiDataCollectionUUID }
  );

  useEffect(() => {
    if (apiDataCollectionFromAPI) {
      setApiDataCollection(apiDataCollectionFromAPI);
      setJsonText({ data: apiDataCollectionFromAPI.RawJson || "" });
    }
  }, [apiDataCollectionFromAPI]);

  const getExampleJSON = async () => {
    const res = await proxyApiTry({
      prefix: "logic/workflows/api",
      url: "api-try",
      method: "POST",
      body: {
        API: {
          ...apiDataCollection.ApiProfiles[0],
          BaseUrl:
            apiDataCollection.ApiProfiles[0]?.BaseUrl + apiDataCollection.Path,
        },
      },
    });
    if (res) {
      setJsonText(res);
    }
  };

  let parsedJSON;
  try {
    parsedJSON = JSON.parse(jsonText?.data || "");
  } catch (error) {
    parsedJSON = null;
  }

  return (
    <div className="mb-5">
      <PageHeader
        heading={"Data Structure"}
        subheading={""}
        children={undefined}
        Icon={ApiIcon}
      />
      <div className="row">
        <div className="col-12 col-md-7">
          <div className="mb-3">
            <label className="form-label">Label</label>
            <input
              type="text"
              className="form-control"
              value={apiDataCollection.Label}
              onChange={(e) =>
                setApiDataCollection({
                  ...apiDataCollection,
                  Label: e.target.value,
                })
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Path </label>
            <InputGroup>
              {apiDataCollection.ApiProfiles[0]?.BaseUrl ? (
                <InputGroupText>
                  {apiDataCollection.ApiProfiles[0]?.BaseUrl}
                </InputGroupText>
              ) : null}
              <input
                type="text"
                className="form-control"
                value={apiDataCollection.Path}
                onChange={(e) =>
                  setApiDataCollection({
                    ...apiDataCollection,
                    Path: e.target.value,
                  })
                }
              />
            </InputGroup>
          </div>
          <div className="p-3 rounded-3 bg-white mb-3">
            <table className="table">
              <thead>
                <tr>
                  <th>Data Label</th>
                  <th>Path</th>
                  <th>Default Value</th>
                  <th className="text-end">
                    <Button
                      variant="outline-secondary"
                      className="text-decoration-none border-0"
                      onClick={() => {
                        setSelected("new");
                      }}
                    >
                      <FontAwesomeIcon icon={faPlusCircle} /> Add Data Field
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(apiDataCollection.Fields || []).map((field, i) => (
                  <tr key={i}>
                    <td>{field.Name}</td>
                    <td>{field.Path}</td>
                    <td>{field.DefaultValue}</td>
                    <td className="text-end">
                      <Button
                        variant="outline-secondary"
                        className="text-decoration-none border-0"
                        onClick={() => setSelected(i)}
                      >
                        <FontAwesomeIcon icon={faEdit} /> Edit
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="text-decoration-none border-0 ms-2"
                        onClick={() => {
                          setApiDataCollection({
                            ...apiDataCollection,
                            Fields: (apiDataCollection.Fields || []).filter(
                              (f, idx) => i !== idx
                            ),
                          });
                          setSelected(undefined);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-12 col-md-5">
          <div>
            <div>
              <div>
                Please enter example JSON or{" "}
                <Button
                  variant="link"
                  onClick={getExampleJSON}
                  // disabled={!apiDataCollection.Uuid}
                  className="p-0 align-baseline"
                >
                  Fetch example Json from API{" "}
                </Button>
              </div>
              {!!jsonText?.data?.length && (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="badge bg-secondary">
                    {jsonText?.data.length} characters
                  </span>
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-auto"
                    onClick={() =>
                      setJsonText({
                        data: JSON.stringify(
                          JSON.parse(jsonText?.data || ""),
                          null,
                          4
                        ),
                      })
                    }
                    disabled={!parsedJSON}
                  >
                    Format
                  </button>
                </div>
              )}
            </div>
            {jsonText?.code && (
              <>
                <h5>API Response</h5>
                <div className="mb-2">
                  Response code:{" "}
                  <span
                    className={`badge ${
                      !!jsonText?.code &&
                      jsonText.code >= 200 &&
                      jsonText.code < 300
                        ? "bg-success"
                        : "bg-danger"
                    }`}
                  >
                    {jsonText?.code}
                  </span>
                </div>
                <div className="d-flex gap-2 mb-2">
                  <div className="text-nowrap">Response message: </div>
                  <div>{jsonText?.message}</div>
                </div>
              </>
            )}

            <textarea
              style={{ height: 500 }}
              className="form-control"
              value={jsonText?.data || ""}
              onChange={(e) => setJsonText({ data: e.target.value })}
            />
            {!parsedJSON && (
              <div className="my-2 alert alert-warning">
                Please enter a valid JSON
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 d-flex justify-content-end ">
        <Button onClick={handleSaveApiDataCollection} variant="colored">
          Save Data Structure
        </Button>
      </div>
      <Modal
        show={selected !== undefined}
        size="xl"
        contentClassName="bg-purple-gradient p-3"
        onHide={() => setSelected(undefined)}
      >
        {selected !== undefined && (
          <div>
            {
              <ApiData
                editedField={
                  selected === "new"
                    ? {
                        Name: "",
                        Path: "",
                        DataType: "string",
                        DefaultValue: "",
                        IsArray: false,
                        RawJson: "",
                      }
                    : apiDataCollection.Fields[selected]
                }
                jsonText={jsonText?.data || ""}
                onSave={(f: ApiDataType) => {
                  if (selected === "new") {
                    setApiDataCollection({
                      ...apiDataCollection,
                      Fields: [...(apiDataCollection.Fields || []), f],
                    });
                  } else {
                    setApiDataCollection({
                      ...apiDataCollection,
                      Fields: apiDataCollection.Fields.map((field, i) =>
                        i === selected ? f : field
                      ),
                    });
                  }
                  setSelected(undefined);
                }}
                close={() => setSelected(undefined)}
              />
            }
          </div>
        )}
      </Modal>
      <JsonCollapse json={apiDataCollection} />
    </div>
  );
}
